// Draft legal policies for Senior Stylist. These are DRAFTS and should be
// reviewed by a qualified solicitor before going live. Company registration
// details are placeholders in [square brackets].

export const EFFECTIVE_DATE = "June 2026";

export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    slug: "privacy",
    intro:
      "This Privacy Policy explains how Senior Stylist collects, uses, shares and protects your personal data when you use our website and mobile app. We are committed to protecting your privacy and handling your data in line with the UK GDPR and the Data Protection Act 2018.",
    sections: [
      {
        heading: "1. Who we are",
        body: [
          "Senior Stylist (\"we\", \"us\", \"our\") operates the Senior Stylist website and mobile application, an on-demand, message-based hair consultation subscription service based in London, United Kingdom.",
          "Data controller: SS Tech Holdings Limited (director: Robert Edwards), company number 16888120, 85 Great Portland Street, London, W1W, United Kingdom.",
          "For any privacy questions or to exercise your rights, contact us at support@senior-stylist.com.",
        ],
      },
      {
        heading: "2. The data we collect",
        body: [
          "Account & identity data: your phone number (used to sign in with a one-time code), and any name or profile details you provide.",
          "Consultation data: messages, photos and hair-related information you choose to share with our stylists through the app.",
          "Transaction data: subscription and billing information (payments are processed securely by Stripe; we do not store full card details).",
          "Technical & usage data: device type, operating system, IP address, and how you interact with our website and app.",
          "Communications: emails and support messages you send to us.",
        ],
      },
      {
        heading: "3. How and why we use your data (lawful bases)",
        body: [
          "To provide the service (performance of a contract): creating your account, matching you to a stylist, delivering consultations and managing your subscription.",
          "To communicate with you (contract / legitimate interests): sending verification codes, service updates and responding to enquiries.",
          "To improve and secure our service (legitimate interests): analytics, troubleshooting, fraud prevention and keeping our platform safe.",
          "For marketing (consent): only where you have opted in. You can withdraw consent at any time.",
          "To meet legal obligations (legal obligation): accounting, tax and responding to lawful requests.",
        ],
      },
      {
        heading: "4. Who we share data with",
        body: [
          "Certified stylists engaged to provide your consultation.",
          "Service providers who help us operate, including cloud hosting, database, analytics, communications and payment providers, acting under contract on our behalf.",
          "Professional advisers, regulators or authorities where required by law.",
          "We never sell your personal data.",
        ],
      },
      {
        heading: "5. International transfers",
        body: [
          "Where any provider processes data outside the UK, we ensure appropriate safeguards are in place, such as a UK adequacy decision or the UK International Data Transfer Agreement / Addendum.",
        ],
      },
      {
        heading: "6. How long we keep your data",
        body: [
          "We keep account and consultation data for as long as your account is active and for a reasonable period afterwards to handle queries and meet legal obligations.",
          "Billing records are kept for 6 years to comply with UK tax law. Verification codes are deleted shortly after use or expiry.",
        ],
      },
      {
        heading: "7. Your rights",
        body: [
          "You have the right to access, rectify, erase, restrict or object to processing, to data portability, and to withdraw consent where processing is based on consent.",
          "To exercise any right, email support@senior-stylist.com. We will respond within one month.",
        ],
      },
      {
        heading: "8. Complaints",
        body: [
          "If you have a concern, please contact us first at support@senior-stylist.com and we will respond within 30 days.",
          "You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk or 0303 123 1113.",
        ],
      },
      {
        heading: "9. Cookies",
        body: [
          "Our website uses cookies and similar technologies. Please see our Cookies Policy for details and to manage your preferences.",
        ],
      },
      {
        heading: "10. Changes to this policy",
        body: [
          "We may update this policy from time to time. The latest version will always be available on this page with the effective date shown above.",
        ],
      },
    ],
  },

  cookies: {
    title: "Cookies Policy",
    slug: "cookies",
    intro:
      "This Cookies Policy explains what cookies are, how Senior Stylist uses them, and how you can manage your preferences. We use cookies in line with the Privacy and Electronic Communications Regulations (PECR) and the UK GDPR.",
    sections: [
      {
        heading: "1. What are cookies?",
        body: [
          "Cookies are small text files placed on your device when you visit a website. Similar technologies include local storage and pixels. They help websites work, remember your choices and understand how the site is used.",
        ],
      },
      {
        heading: "2. Types of cookies we use",
        body: [
          "Strictly necessary cookies: required for the site to function, such as keeping you signed in and keeping the site secure. These do not require consent.",
          "Functional cookies: remember your preferences and choices.",
          "Analytics cookies: help us understand how visitors use the site so we can improve it. These are only set with your consent.",
          "We do not use cookies for third-party advertising.",
        ],
      },
      {
        heading: "3. Managing your consent",
        body: [
          "When you first visit our site you can Accept or Reject non-essential cookies using our cookie banner. Strictly necessary cookies are always on.",
          "You can change your choice at any time by clicking the \"Cookie settings\" link and by adjusting your browser settings to block or delete cookies.",
          "Blocking some cookies may affect how the website works.",
        ],
      },
      {
        heading: "4. Third parties",
        body: [
          "Some cookies may be set by trusted providers who help us run analytics or deliver core functionality. These providers act under contract and only process data as instructed.",
        ],
      },
      {
        heading: "5. More information",
        body: [
          "For how we handle personal data more broadly, see our Privacy Policy. Questions? Email support@senior-stylist.com.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms & Conditions",
    slug: "terms",
    intro:
      "These Terms & Conditions govern your use of the Senior Stylist website and mobile app and the consultation subscription service we provide. By using our service you agree to these terms. Please read them carefully.",
    sections: [
      {
        heading: "1. About us",
        body: [
          "Senior Stylist is operated by SS Tech Holdings Limited (director: Robert Edwards), company number 16888120, registered in England and Wales at 85 Great Portland Street, London, W1W, United Kingdom. You can contact us at support@senior-stylist.com.",
        ],
      },
      {
        heading: "2. Our service",
        body: [
          "Senior Stylist is an on-demand, message-based hair consultation subscription service. Our certified stylists provide personalised guidance and recommendations through the app.",
          "Consultations provide general hair-care guidance only and are not a substitute for in-person professional or medical treatment. For any medical or scalp health condition you should consult a doctor or trichologist.",
        ],
      },
      {
        heading: "3. Accounts",
        body: [
          "To use the service you must create an account and sign in using your phone number and a one-time verification code. You are responsible for keeping access to your phone and account secure.",
          "You must be at least 18 years old, or have the consent of a parent or guardian, to use the service.",
        ],
      },
      {
        heading: "4. Subscriptions, payment and cancellation",
        body: [
          "The service is provided on a subscription basis. Current plans and prices are shown in the app at the point of purchase.",
          "Subscriptions are billed securely through Stripe and renew automatically unless cancelled. You can cancel your subscription at any time; refunds are subject to conditions, contact support@senior-stylist.com for refund requests.",
          "Your statutory rights as a consumer under the Consumer Rights Act 2015 are not affected.",
        ],
      },
      {
        heading: "5. Acceptable use",
        body: [
          "You agree to use the service lawfully and respectfully, to provide accurate information, and not to misuse, disrupt or attempt to gain unauthorised access to our platform.",
          "You retain ownership of content you share, but grant us a licence to use it as needed to provide the service.",
        ],
      },
      {
        heading: "6. Intellectual property",
        body: [
          "All content, branding and materials on our website and app are owned by or licensed to Senior Stylist and may not be copied or reused without permission.",
        ],
      },
      {
        heading: "7. Liability",
        body: [
          "We provide the service with reasonable care and skill. To the extent permitted by law, we are not liable for outcomes resulting from advice you choose to follow, or for indirect or unforeseeable losses.",
          "Nothing in these terms limits liability that cannot be limited by law.",
        ],
      },
      {
        heading: "8. Governing law",
        body: [
          "These terms are governed by the laws of England and Wales, and disputes are subject to the courts of England and Wales.",
        ],
      },
      {
        heading: "9. Changes and contact",
        body: [
          "We may update these terms from time to time; the current version is always available on this page. For any questions, contact support@senior-stylist.com.",
        ],
      },
    ],
  },
};
